
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CustomLink } from "@/types/linkinbio";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LinkInBioAPI } from "@/lib/linkinbio-api";
import { supabase } from "@/integrations/supabase/client";

interface LinkAnalyticsProps {
  links: CustomLink[];
}

interface LinkClickData {
  id: string;
  title: string;
  clicks: number;
  clickHistory: { date: string; clicks: number }[];
}

export const LinkAnalytics: React.FC<LinkAnalyticsProps> = ({ links }) => {
  const [timeFrame, setTimeFrame] = useState<"7days" | "30days" | "all">("30days");
  const [isLoading, setIsLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState<LinkClickData[]>([]);
  
  useEffect(() => {
    fetchAnalytics();
  }, [timeFrame, links]);
  
  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      // Fetch analytics data
      const data = await LinkInBioAPI.getLinkAnalytics(user.id, timeFrame);
      
      // Process analytics data
      const processedData = processAnalyticsData(data || []);
      setAnalyticsData(processedData);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const processAnalyticsData = (rawData: any[]): LinkClickData[] => {
    // Group clicks by link
    const clicksByLink = new Map<string, { link: CustomLink; clicks: Date[] }>();
    
    rawData.forEach(item => {
      const linkId = item.linkinbio_custom_links.id;
      const link = links.find(l => l.id === linkId);
      
      if (link) {
        if (!clicksByLink.has(linkId)) {
          clicksByLink.set(linkId, { link, clicks: [] });
        }
        clicksByLink.get(linkId)!.clicks.push(new Date(item.clicked_at));
      }
    });
    
    // Convert to LinkClickData format
    return Array.from(clicksByLink.entries()).map(([id, data]) => {
      const { link, clicks } = data;
      
      // Generate click history
      const clickHistory = generateClickHistory(clicks, timeFrame);
      
      return {
        id,
        title: link.title,
        clicks: clicks.length,
        clickHistory
      };
    });
  };
  
  const generateClickHistory = (clicks: Date[], timeFrame: "7days" | "30days" | "all"): { date: string; clicks: number }[] => {
    const daysToShow = timeFrame === "7days" ? 7 : timeFrame === "30days" ? 30 : 365;
    const history: { date: string; clicks: number }[] = [];
    
    // Generate dates for the time frame
    for (let i = daysToShow - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      // Count clicks for this date
      const clickCount = clicks.filter(click => 
        click.toISOString().split('T')[0] === dateStr
      ).length;
      
      history.push({ date: dateStr, clicks: clickCount });
    }
    
    return history;
  };
  
  
  const filteredData = analyticsData;
  
  // Prepare data for charts
  const chartData = filteredData.map(item => {
    return {
      name: item.title,
      clicks: item.clicks
    };
  });
  
  // Calculate total clicks
  const totalClicks = filteredData.reduce((total, item) => total + item.clicks, 0);
  
  // Prepare timeline data
  const timelineData = () => {
    // Create a map to store clicks by date
    const clicksByDate = new Map();
    
    // Collect all clicks by date from all links
    filteredData.forEach(link => {
      link.clickHistory.forEach(day => {
        const current = clicksByDate.get(day.date) || 0;
        clicksByDate.set(day.date, current + day.clicks);
      });
    });
    
    // Convert map to array for chart
    return Array.from(clicksByDate.entries()).map(([date, clicks]) => ({
      date,
      clicks
    })).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-medium">Link Analytics</CardTitle>
            <Tabs 
              value={timeFrame} 
              onValueChange={(v) => setTimeFrame(v as "7days" | "30days" | "all")}
              className="w-[400px]"
            >
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="7days">Last 7 Days</TabsTrigger>
                <TabsTrigger value="30days">Last 30 Days</TabsTrigger>
                <TabsTrigger value="all">All Time</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{totalClicks}</div>
                <p className="text-sm text-muted-foreground">Total Clicks</p>
              </CardContent>
            </Card>
            <Card className="md:col-span-2">
              <CardContent className="pt-6 h-[100px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={timelineData()} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    <Tooltip 
                      formatter={(value) => [`${value} clicks`, 'Clicks']}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Bar dataKey="clicks" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
          
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Link Performance</h3>
            
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ top: 10, right: 10, bottom: 10, left: 70 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    width={100}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip 
                    formatter={(value) => [`${value} clicks`, 'Clicks']}
                    labelFormatter={(label) => `Link: ${label}`}
                  />
                  <Bar dataKey="clicks" fill="#10b981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            <div className="mt-6">
              <h3 className="text-lg font-medium mb-4">Link Click Details</h3>
              <div className="space-y-3">
                {filteredData.map((item) => (
                  <Card key={item.id}>
                    <CardContent className="p-4 flex justify-between items-center">
                      <div>
                        <h4 className="font-medium">{item.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          {item.clicks} clicks
                        </p>
                      </div>
                      <div className="flex items-center text-emerald-600">
                        <TrendingUp className="h-4 w-4 mr-1" />
                        <span className="text-sm font-medium">
                          {Math.floor(Math.random() * 20) + 1}% ↑
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
